// CommentThread — threaded discussion on ANY entity (VaultOS v2 design §3.5).
//
// Bound to ONE `entityRef` ({ type, id } or the `ref:<type>:<id>` string form,
// functions/entityRef.js) and backed by the shared comments spine
// (`sdk.execute('comments', 'list' | 'create' | 'update' | 'remove', …)` →
// /core/comments/*). Because it keys on the uniform ref, the SAME component
// serves a canvas entity node, a training record, a document, a mail thread
// and anything a later surface registers — there is no per-entity copy.
//
// MOUNT IT BY BARE KEY from any package:
//   CommentThread: { entityRef: { type: 'record', id: 'vault.items/i-1' } }
//   CommentThread: { entityRef: (el, s) => s.selectedRef, canManageOthers: true }
//
// `canManageOthers` mirrors the server's role gate (editor-or-above may touch
// another member's comment; an author may always touch their own). It is a
// PROP, not a lookup: this package cannot know how a given consumer resolves
// its viewer's workspace role, and guessing would either hide a button the
// server allows or offer one it refuses. Left unset the thread offers delete
// on the viewer's OWN comments only — the safe half, and the true one for a
// plain member.
//
// ── FA204 / frank trap ──────────────────────────────────────────────────────
// Every helper is a `scope` method called as `el.scope.<name>`, never a
// module-scope const read from inside a factory: those read as undefined once
// this component is frank.toJSON'd for platform delivery. The pure reducers
// live in functions/commentThread.js and are reached with `el.call`, which is
// a runtime string lookup and therefore brender-safe.
//
// ── DESIGN ──────────────────────────────────────────────────────────────────
// Flat and borderless (design-direction §8): rows separate by fill contrast
// and whitespace, never by a hairline. One nesting level only — a thread is a
// conversation, not a tree (see commentThread.js for the reducer's reasoning).
// Zero shadows; the host sheet owns the elevation.
export const CommentThread = {
  scope: {
    // THE host resolver. `el.scope` is inherited by every descendant, so a
    // button four levels down calls the same methods — but `entityRef` and
    // `canManageOthers` are props on the MOUNT, and the component's own state
    // bag lives there too. Every method below starts here rather than counting
    // `el.parent` hops, which is what breaks the moment a wrapper is inserted.
    // Falls back to the passed element so a method never operates on null.
    hostOf(el) {
      let n = el
      while (n) {
        if (n.entityRef) return n
        n = n.parent
      }
      return el
    },

    // The `ref:<type>:<id>` identity this mount is bound to. Also the LATCH
    // key: the thread reloads when — and only when — the ref changes.
    refKey(el) {
      try {
        return el.call('formatEntityRef', el.entityRef) || ''
      } catch (_) {
        return ''
      }
    },

    // Load the thread for the mount `el` belongs to. Callable from any
    // descendant — `this.hostOf` resolves the mount and its state bag.
    // Idempotent per ref: the lifecycle hooks latch on `el._threadFor`, so a
    // re-render never re-fetches.
    async load(el) {
      const host = this.hostOf(el)
      const s = host.state
      if (!s || typeof s.update !== 'function') return
      let filter = null
      try {
        filter = host.call('commentListFilter', host.entityRef)
      } catch (_) {
        filter = null
      }
      if (!filter) {
        s.update({ rows: [], loading: false, error: '', errorOp: '' })
        return
      }
      const sdk = host.sdk
      if (!sdk || typeof sdk.execute !== 'function') {
        // No SDK on this host (docs, preview, a storybook mount) — render the
        // empty state rather than an error the reader can do nothing about.
        s.update({ rows: [], loading: false, error: '', errorOp: '' })
        return
      }
      s.update({ loading: true, error: '', errorOp: '' })
      try {
        const raw = await sdk.execute('comments', 'list', filter)
        s.update({
          rows: host.call('commentThreadRows', raw, {
            email: (s.root && s.root.currentUserEmail) || '',
            canManageOthers: !!host.canManageOthers
          }),
          loading: false,
          error: '',
          errorOp: ''
        })
      } catch (err) {
        s.update({
          rows: [],
          loading: false,
          error: (err && err.message) || 'error',
          errorOp: 'load'
        })
      }
    },

    // Post the composer's draft, as a reply when one is aimed. Clears the
    // draft only AFTER the server accepts it — user input is never disposable,
    // and a failed post that also ate the text is the worst of both.
    async post(el) {
      const host = this.hostOf(el)
      const s = host.state
      if (!s || typeof s.update !== 'function' || s.busy) return
      let payload = null
      try {
        payload = host.call('commentCreatePayload', host.entityRef, s.draft, s.replyTo)
      } catch (_) {
        payload = null
      }
      if (!payload) return
      const sdk = host.sdk
      if (!sdk || typeof sdk.execute !== 'function') return
      s.update({ busy: true, error: '', errorOp: '' })
      try {
        await sdk.execute('comments', 'create', { payload })
        s.update({ draft: '', replyTo: null, replyToName: '', busy: false })
        await this.load(host)
      } catch (err) {
        // The draft is deliberately NOT cleared on failure — user input is
        // never disposable, and a post that ate the text is worse than one
        // that just failed.
        s.update({ busy: false, error: (err && err.message) || 'error', errorOp: 'post' })
      }
    },

    // Soft-delete. The server keeps the row so replies survive; the reducer
    // then drops the tombstone unless it has children.
    async remove(el, id) {
      const host = this.hostOf(el)
      const s = host.state
      if (!id || !s || typeof s.update !== 'function' || s.busy) return
      const sdk = host.sdk
      if (!sdk || typeof sdk.execute !== 'function') return
      s.update({ busy: true, error: '', errorOp: '' })
      try {
        await sdk.execute('comments', 'remove', { id })
        s.update({ busy: false })
        await this.load(host)
      } catch (err) {
        s.update({ busy: false, error: (err && err.message) || 'error', errorOp: 'remove' })
      }
    },

    // Aim the composer at a comment (or clear the aim with a null id).
    aimReply(el, id, name) {
      const host = this.hostOf(el)
      const s = host.state
      if (!s || typeof s.update !== 'function') return
      s.update({ replyTo: id || null, replyToName: name || '' })
    }
  },

  // `rows` holds the REDUCED list (commentThreadRows output), not the raw
  // response: the reduction runs once per load instead of once per paint, and
  // every child factory reads one shape. Every key is declared here so a
  // child's state walk-up stops at this component instead of running to the
  // root — the same trap the simone composer's `images` note records.
  state: {
    rows: [],
    loading: false,
    error: '',
    // WHICH operation failed. One `error` string used to serve all three write
    // paths, so a rejected POST printed "Comments could not be loaded." — a
    // sentence about the wrong half of the component, and one that also hid
    // the fact that the list had loaded fine. Measured live on 2026-09-07:
    // the comments spine accepted `list` for a `record` entityRef and refused
    // `create` for the same ref, and the thread reported a load failure.
    // Declared here, like every other key, so a child's state walk-up stops at
    // this component (v3.14 drops an undeclared key on `update`).
    errorOp: '',
    draft: '',
    replyTo: null,
    replyToName: '',
    busy: false
  },

  flow: 'y',
  gap: 'Z',
  width: '100%',
  minWidth: '0',

  // The ref can arrive as a reactive prop, so the load is latched on the
  // resolved key rather than fired once: `onInit` covers a mount that already
  // knows its ref, `onUpdate` covers a sheet whose ref lands one paint later
  // or changes when the user selects a different node. `el._threadFor` is an
  // INSTANCE flag, never root state — root.update silently drops any key the
  // consumer's schema does not declare, and this is not state anyone else
  // reads (the same technique the chat Composer's `_dragOver` uses).
  onInit: (el, s) => {
    const key = el.scope.refKey(el)
    if (!key || el._threadFor === key) return
    el._threadFor = key
    el.scope.load(el)
  },
  onUpdate: (el, s) => {
    const key = el.scope.refKey(el)
    if (!key || el._threadFor === key) return
    el._threadFor = key
    s.update({ draft: '', replyTo: null, replyToName: '' })
    el.scope.load(el)
  },

  Head: {
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    width: '100%',
    minWidth: '0',
    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      text: '{{ shell.comments.title | polyglot }}'
    },
    Count: {
      tag: 'span',
      if: (el, s) => !!(s.rows && s.rows.length),
      text: (el, s) => String(s.rows.length),
      fontSize: 'Z1',
      color: 'caption'
    }
  },

  Status: {
    tag: 'p',
    // One line for all three quiet states, in priority order: an error the
    // reader should see, the load, then "nothing here yet". `hide` not `if` —
    // the line keeps its slot so the list never jumps as the state settles.
    hide: (el, s) => !(s.error || s.loading || !(s.rows && s.rows.length)),
    margin: '0',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) =>
      s.error
        ? el.call(
            'polyglot',
            s.errorOp === 'post'
              ? 'shell.comments.postError'
              : s.errorOp === 'remove'
                ? 'shell.comments.removeError'
                : 'shell.comments.error'
          )
        : s.loading
          ? el.call('polyglot', 'shell.comments.loading')
          : el.call('polyglot', 'shell.comments.empty')
  },

  List: {
    flow: 'y',
    gap: 'Y',
    width: '100%',
    minWidth: '0',
    childrenAs: 'state',
    children: (el, s) => (s.rows || []).map((r) => ({ key: r.id, ...r })),
    childExtends: 'CommentThreadItem'
  },

  Composer: {
    flow: 'y',
    gap: 'X',
    width: '100%',
    minWidth: '0',

    ReplyingTo: {
      tag: 'button',
      type: 'button',
      hide: (el, s) => !s.replyTo,
      alignSelf: 'flex-start',
      flow: 'x',
      align: 'center center',
      gap: 'Y',
      padding: 'X Z',
      round: 'radiusPill',
      background: 'overlay',
      borderWidth: '0',
      cursor: 'pointer',
      fontSize: 'X',
      color: 'caption',
      ':hover': { background: 'hover', color: 'title' },
      aria: { label: (el) => el.call('polyglot', 'shell.comments.cancelReply') },
      Label: {
        tag: 'span',
        text: (el, s) =>
          el.call('polyglot', 'shell.comments.replyingTo').replace('{name}', s.replyToName || '')
      },
      X: { tag: 'span', text: '✕' },
      onClick: (e, el) => {
        e.preventDefault()
        el.scope.aimReply(el, null, '')
      }
    },

    Field: {
      tag: 'textarea',
      rows: '2',
      resize: 'none',
      width: '100%',
      minWidth: '0',
      padding: 'Z Z1',
      round: 'radiusControl',
      background: 'overlay',
      border: 'none',
      outline: 'none',
      color: 'title',
      fontSize: 'Z1',
      fontFamily: 'inherit',
      ':focus-visible': { shadow: 'none' },
      placeholder: (el) => el.call('polyglot', 'shell.comments.placeholder'),
      value: (el, s) => s.draft || '',
      disabled: (el, s) => !!s.busy,
      onInput: (e, el, s) => s.update({ draft: e.target.value }),
      onKeydown: (e, el, s) => {
        // Cmd/Ctrl+Enter posts; plain Enter keeps writing. A comment is prose,
        // not a chat line — the opposite convention to the simone composer,
        // and the one every long-form comment box uses.
        if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return
        e.preventDefault()
        el.scope.post(el)
      }
    },

    PostBtn: {
      tag: 'button',
      type: 'button',
      alignSelf: 'flex-end',
      padding: 'Y Z1',
      minHeight: 'controlHCompact',
      round: 'radiusPill',
      borderWidth: '0',
      cursor: 'pointer',
      fontSize: 'Y2',
      fontWeight: '700',
      // Solid house chip, same surface the assistant's follow-up pills use —
      // fill-contrast separation, no outline.
      theme: 'layer',
      color: 'title',
      transition: 'B defaultBezier background',
      ':hover': { background: 'hover' },
      disabled: (el, s) => !!s.busy || !String(s.draft || '').trim(),
      isIdle: (el, s) => !String(s.draft || '').trim(),
      '.isIdle': { opacity: '.5', cursor: 'default' },
      text: '{{ shell.comments.post | polyglot }}',
      onClick: (e, el) => {
        e.preventDefault()
        el.scope.post(el)
      }
    }
  }
}
